/**
 * ALBRTA-b1 · «Alojamientos que el plan no recoge» (sección propia de P3).
 *
 * Fuera del modelo de cotejo (DOP-3 a′ de `ALBRTA`): no es un estado de `EstadoCotejo` ni
 * mueve la «Cobertura del catálogo». Muestra el alojamiento reglado del Registro de Turismo
 * de Andalucía (OpenRTA) que el plan no recoge, puesto en perspectiva frente a las cifras del
 * propio plan (aforo declarado de aptos y plazas DGPC, `totalesAforos`, las mismas de P4).
 * Declarado, DGPC y registro nunca se suman entre sí.
 *
 * DOP de `ALBRTA-b1` (Luis, 1-oct-2026, «adelante»): carga con hook propio (1 a), sección
 * plegable al final de la lista (3 a), sin mapa (4 b), los emparejados solo se cuentan (5 a),
 * 404 y fallo se distinguen (6 a).
 */
import { useEffect, useMemo, useState, type CSSProperties, type FC } from 'react';
import { colors, typography, borderRadius, spacing } from '@/design-system/v2';
import type { ProcessedRecord } from '@/types/export.types';
import { ATRIBUCION_OPENRTA } from '@/types/openrta';
import {
  getOpenRTAR2Service, type ConsultaOpenRTA,
} from '@/services/geocoding/specialized/OpenRTAR2Service';
import {
  resumirAlojamientosRTA, rotuloCategoriaRTA, ROTULO_TIPO_RTA, type CruceAlojamientoRTA,
} from '@/lib/albergue/alojamientosRTA';
import { seleccionarAforos, totalesAforos } from '@/lib/albergue/aforosAlbergue';
import { cifraRAE } from '@/lib/albergue/fichaAlbergue';

type EstadoCarga = { estado: 'cargando' } | ConsultaOpenRTA;

/** DOP-1 a: carga del registro del municipio (un fichero de pocos KB, no bloquea P3). */
export function useAlojamientosRTA(ine5: string): EstadoCarga {
  const [carga, setCarga] = useState<EstadoCarga>({ estado: 'cargando' });
  useEffect(() => {
    let vivo = true;
    setCarga({ estado: 'cargando' });
    getOpenRTAR2Service().consultarMunicipio(ine5).then((c) => { if (vivo) setCarga(c); });
    return () => { vivo = false; };
  }, [ine5]);
  return carga;
}

const plural = (n: number, uno: string, varios: string) => `${cifraRAE(n)} ${n === 1 ? uno : varios}`;

const S: Record<string, CSSProperties> = {
  wrap: {
    marginTop: '28px', padding: `${spacing[3]} ${spacing[4]}`,
    border: `1px solid ${colors.border.DEFAULT}`, borderRadius: borderRadius.md,
    background: colors.bg.secondary, fontFamily: typography.fontFamily.sans,
  },
  cabecera: {
    display: 'flex', alignItems: 'baseline', gap: '8px', width: '100%', padding: 0,
    background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
    color: colors.fg.primary, fontFamily: typography.fontFamily.sans,
    fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold,
  },
  cifras: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.normal, color: colors.fg.secondary },
  texto: { fontSize: typography.fontSize.sm, color: colors.fg.secondary, margin: '8px 0 0' },
  nota: { fontSize: typography.fontSize.xs, color: colors.fg.muted, margin: '8px 0 0' },
  lista: { listStyle: 'none', margin: '10px 0 0', padding: 0 },
  fila: { padding: '8px 0', borderTop: `1px solid ${colors.border.DEFAULT}` },
  nombre: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: colors.fg.primary },
  detalle: { fontSize: typography.fontSize.xs, color: colors.fg.muted, marginTop: '2px' },
  aviso: { fontSize: typography.fontSize.xs, color: colors.warning, marginTop: '2px' },
};

/** ALBRTA-b2c-3b: la regla de «Única» vive en la pieza común (la comparten XLSX y ODS). */
export { rotuloCategoriaRTA };

const FilaAlojamiento: FC<{ cruce: CruceAlojamientoRTA }> = ({ cruce }) => {
  const a = cruce.alojamiento;
  const categoria = rotuloCategoriaRTA(a.categoria);
  const partes = [
    ROTULO_TIPO_RTA[a.tipo] + (categoria ? ` (${categoria})` : ''),
    a.plazas != null ? plural(a.plazas, 'plaza del registro', 'plazas del registro') : 'sin plazas en el registro',
    a.direccion,
  ].filter(Boolean);
  return (
    <li data-testid="rta-alojamiento" data-estado={cruce.estado} style={S.fila}>
      <div style={S.nombre}>{a.nombre ?? 'Sin nombre en el registro'}</div>
      <div style={S.detalle}>{partes.join(' · ')}</div>
      {cruce.avisos.map((t) => <div key={t} style={S.aviso}>⚠ {t}</div>)}
    </li>
  );
};

export interface AlojamientosRTASectionProps {
  ine5: string;
  /** Filas del plan sin plantillas (las mismas que exporta P4). */
  registrosPlan: ProcessedRecord[];
}

export const AlojamientosRTASection: FC<AlojamientosRTASectionProps> = ({ ine5, registrosPlan }) => {
  const carga = useAlojamientosRTA(ine5);
  const [abierta, setAbierta] = useState(true);
  const plan = useMemo(() => totalesAforos(seleccionarAforos(registrosPlan)), [registrosPlan]);
  const resumen = useMemo(
    () => (carga.estado === 'con_registro'
      ? resumirAlojamientosRTA(carga.datos.alojamientos, carga.datos.viviendas, registrosPlan)
      : null),
    [carga, registrosPlan],
  );

  const n = resumen ? resumen.propuestos.length + resumen.revisar.length : 0;
  const titulo = 'Alojamientos que el plan no recoge';
  let cifras = '';
  if (carga.estado === 'cargando') cifras = 'consultando el registro…';
  else if (resumen && n > 0) {
    // DOP-6 (ALBRTA-b2a): sin «· 0 · 0» cuando no hay nada que proponer; lo dice el cuerpo.
    cifras = `· ${cifraRAE(n)} · ${plural(resumen.plazasPropuestas, 'plaza del registro', 'plazas del registro')}`;
    if (resumen.enElPlan) cifras += ` · y ${cifraRAE(resumen.enElPlan)} ya en el plan`;
  }

  const viv = resumen?.viviendas;
  const nViv = viv ? viv.vut + viv.vtar : 0;

  return (
    <section data-testid="rta-seccion" data-estado={carga.estado} style={S.wrap} aria-label={titulo}>
      <button type="button" style={S.cabecera} aria-expanded={abierta} onClick={() => setAbierta((v) => !v)}>
        <span aria-hidden>{abierta ? '▾' : '▸'}</span>
        <span>{titulo}</span>
        {cifras && <span data-testid="rta-cifras" style={S.cifras}>{cifras}</span>}
      </button>

      {abierta && (
        <div data-testid="rta-cuerpo">
          {carga.estado === 'fallo' && (
            <p style={S.nota}>No se pudo consultar el Registro de Turismo de Andalucía. El cotejo no se ve afectado.</p>
          )}
          {(carga.estado === 'sin_registro' || (resumen && resumen.propuestos.length + resumen.revisar.length + resumen.enElPlan === 0)) && (
            <p style={S.texto}>El registro no tiene alojamiento reglado en este municipio.</p>
          )}
          {resumen && resumen.enElPlan > 0 && n === 0 && (
            <p style={S.texto}>El plan ya recoge el alojamiento reglado del registro ({cifraRAE(resumen.enElPlan)}).</p>
          )}

          {(carga.estado === 'sin_registro' || resumen) && (
            <p data-testid="rta-perspectiva" style={S.texto}>
              En el plan: {plural(plan.aforoAptos, 'persona', 'personas')} de aforo declarado en elementos aptos
              {' · '}{plural(plan.plazasAptos, 'plaza', 'plazas')} de albergue (DGPC)
              {plan.aptosSinCifra ? ` · ${plural(plan.aptosSinCifra, 'apto sin cifra', 'aptos sin cifra')}` : ''}.
              {' '}No se suman con las plazas del registro.
            </p>
          )}

          {resumen && n > 0 && (
            <ul style={S.lista}>
              {resumen.propuestos.map((c) => <FilaAlojamiento key={c.alojamiento.id} cruce={c} />)}
              {resumen.revisar.map((c) => <FilaAlojamiento key={c.alojamiento.id} cruce={c} />)}
            </ul>
          )}

          {nViv > 0 && viv && (
            <p data-testid="rta-viviendas" style={S.nota}>
              Además, el registro tiene {plural(nViv, 'vivienda turística', 'viviendas turísticas')}
              {' '}({cifraRAE(viv.vut)} de uso turístico y {cifraRAE(viv.vtar)} de alojamiento rural):
              solo se cuentan; el registro no publica sus plazas por municipio.
            </p>
          )}

          {(carga.estado === 'sin_registro' || resumen) && (
            <p style={S.nota}>
              {ATRIBUCION_OPENRTA}. No es un censo completo: las plazas son las del registro publicado.
            </p>
          )}
        </div>
      )}
    </section>
  );
};
